import React, { useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../App';
import './AdminRideCompletion.css';
import { datumUndZeit } from '../lib/zeit';

const euro = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const statusGuidance = {
  searching: 'Noch keine Fahrt begonnen. Fahrer zuweisen oder die vorhandene Admin-Stornierung verwenden.',
  accepted: 'Noch keine Fahrt begonnen. Fahrer neu zuweisen oder die vorhandene Admin-Stornierung verwenden.',
  arrived: 'Noch keine Fahrt begonnen. Fahrer neu zuweisen oder die vorhandene Admin-Stornierung verwenden. Eine Ankunft oder PIN-Fahrt wird hier nicht erfunden.',
  completed: 'Bereits abgeschlossen. Zahlungs- oder Buchungsfehler über die vorhandenen auditierten Finanzkorrekturen prüfen; kein zweiter Fahrtabschluss.',
  cancelled: 'Bereits storniert. Zahlungs- oder Buchungsfehler über die vorhandenen auditierten Finanzkorrekturen prüfen; keine Fahrt nachträglich erfinden.',
};
const completionGuidance = {
  completion_resources_changed: 'Ein Gebühren- oder Zahlungsanspruch hat sich geändert. Abschlussstand laden; einen unbelegten Plan nur mit der sicheren Rücknahme neu berechnen.',
  completion_prepared_state_changed: 'Der vorbereitete Plan passt nicht mehr zur Fahrt. Abschlussstand und Geldbelege prüfen; keinen Betrag erneut kassieren.',
  completion_payment_started: 'Ein Barbeleg oder Zahlungsvorgang ist vorhanden. Erst den tatsächlichen Geldfluss klären; keine neue Abbuchung auslösen.',
  completion_payment_state_requires_review: 'Ein Zahlungsauftrag existiert bereits. Dessen Status im Finanzbuch prüfen; keinen zweiten Einzug anlegen.',
  completion_replan_state_changed: 'Fahrt oder Preis hat sich seit der Rücknahme verändert. Abschlussstand neu laden und den Vorgang fachlich prüfen.',
  completion_resume_snapshot_unavailable: 'Der alte Plan kann nicht verlustfrei zurückgenommen werden. Fahrt und Wartezeit im Prüfprotokoll klären.',
  admin_completion_preview_stale: 'Fahrt oder Betrag hat sich seit der Vorschau geändert. Abschlussstand neu laden und neu prüfen.',
};
// A-1: Diese Antworten bedeuten „Betrag nicht belegt berechenbar“. Der Server entscheidet
// selbst, ob der Notweg zulässig ist (sonst essen_review_not_required).
const reviewOfferCodes = ['essen_settlement_review_required', 'essen_cash_completion_prepare_required'];
// A1-MISCHFAHRT: Für eine Fahrt mit Zieländerung sagt der Server es ausdrücklich (completion_review_available,
// Modus 'unsettled' = nur ohne Abrechnung, nie Schätzpreis).
const reviewOfferFrom = (data) => (data && (reviewOfferCodes.includes(data.error) || data.completion_review_available === true)
  ? { code: data.error, reason: data.reason || null, mode: data.completion_review_mode === 'unsettled' ? 'unsettled' : null } : null);
const failureText = (error, fallback) => completionGuidance[error.response?.data?.error]
  || error.response?.data?.message || fallback;
const validCashState = (r, value) => r && ['assignment_version', 'cash_receipt_version', 'recorded_cash_received_cents',
  'recorded_cash_returned_cents', 'cash_remaining_cents', 'cash_change_due_cents']
  .every(key => Number.isSafeInteger(r[key]) && r[key] >= 0)
  && r.recorded_cash_returned_cents <= r.recorded_cash_received_cents
  && r.cash_remaining_cents === Math.max(0, value.cash_cents - r.recorded_cash_received_cents + r.recorded_cash_returned_cents)
  && r.cash_change_due_cents === Math.max(0, r.recorded_cash_received_cents - r.recorded_cash_returned_cents - value.cash_cents);
const validRecovery = (r, value) => !r || (typeof r.completion_id === 'string' && r.completion_id.length > 0
  && validCashState(r, value) && Number.isSafeInteger(r.effective_end_ms) && r.effective_end_ms === Date.parse(value.effective_end_at));
const validPreview = (value, rideId, reason) => value && value.ride_id === rideId
  && value.reason === reason && value.currency === 'EUR'
  && typeof value.preview_token === 'string' && value.preview_token.length > 0
  && typeof value.effective_end_at === 'string' && Number.isFinite(Date.parse(value.effective_end_at))
  && Number.isFinite(Date.parse(value.expires_at)) && Date.parse(value.expires_at) > Date.now()
  && ['amount_cents', 'cash_cents', 'card_cents', 'wallet_cents', 'open_fee_cash_cents']
    .every((key) => Number.isSafeInteger(value[key]) && value[key] >= 0) && validRecovery(value.recovery, value)
  && ['open_fee_card_cents', 'included_fee_card_cents', 'included_paypal_tip_cents']
    .every((key) => value[key] == null || (Number.isSafeInteger(value[key]) && value[key] >= 0))
  && value.amount_cents === value.cash_cents + value.card_cents + value.wallet_cents
  && (!value.cash_reconciliation || (!value.recovery && validCashState(value.cash_reconciliation, value)))
  && (!value.replan || (typeof value.replan.completion_id === 'string'
    && Number.isSafeInteger(value.replan.effective_end_ms)
    && value.replan.effective_end_ms === Date.parse(value.effective_end_at)));

export default function AdminRideCompletion({ ride, onCompleted }) {
  const { can } = useAuth();
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [cashReceived, setCashReceived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [recoveryState, setRecoveryState] = useState(null);
  const [replanAnchor, setReplanAnchor] = useState(null);
  const [previousAmount, setPreviousAmount] = useState(null);
  const [reviewOffer, setReviewOffer] = useState(null);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [openReview, setOpenReview] = useState(null);
  const [openReviewMixed, setOpenReviewMixed] = useState(false);
  const [resolution, setResolution] = useState(null);
  const pending = useRef(false);
  const resumeRequest = useRef(null);
  const mounted = useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  React.useEffect(() => {
    if (ride.status !== 'completed' || !can('finance.manage')) return;
    api.get(`/rides/${encodeURIComponent(ride.id)}/admin-completion-state`)
      .then(({ data }) => { if (mounted.current && data?.ride?.id === ride.id) { setOpenReview(data.essen_review || null); setOpenReviewMixed(data.essen_review_mixed === true); setResolution(data.essen_review_resolution || null); } })
      .catch(() => { /* Ohne Prüfdaten bleibt die bisherige Statusanzeige. */ });
  }, [ride.id, ride.status]);

  const cashState = preview?.recovery || preview?.cash_reconciliation;
  const cashToConfirm = cashState ? cashState.cash_remaining_cents : preview?.cash_cents;
  const changeDue = cashState?.cash_change_due_cents || 0;
  const serverRide = recoveryState?.ride || ride;
  if (!can('finance.manage')) return null;
  const loadRecovery = async () => {
    const { data } = await api.get(`/rides/${encodeURIComponent(ride.id)}/admin-completion-state`);
    if (data?.ride?.id !== ride.id) throw new Error('completion_state_invalid');
    if (mounted.current) {
      setRecoveryState(data);
      setReplanAnchor(data.replan_anchor || null);
    }
    return data;
  };
  const resumePrepared = async () => {
    const plan = recoveryState?.completion;
    if (pending.current || !reason.trim() || plan?.state !== 'prepared' || plan.cash_receipt_version !== 0) return;
    pending.current = true; setBusy(true); setError('');
    if (resumeRequest.current?.completionId !== plan.id) resumeRequest.current = {
      completionId: plan.id,
      id: `admin-resume-${ride.id}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    };
    try {
      const { data } = await api.post(`/rides/${encodeURIComponent(ride.id)}/admin-completion-resume`, {
        reason: reason.trim(), completion_id: plan.id, assignment_version: plan.assignment_version,
        expected_receipt_version: plan.cash_receipt_version, resume_request_id: resumeRequest.current.id,
      });
      if (data?.ride?.id !== ride.id || !data.replan_anchor || data.replan_anchor.completion_id !== plan.id) {
        throw new Error('completion_resume_response_unclear');
      }
      if (mounted.current) {
        const oldParts = [plan.cash_total_cents, plan.card_cents, plan.open_fee_card_cents,
          plan.included_fee_card_cents, plan.included_paypal_tip_cents, plan.wallet_cents];
        setPreviousAmount(oldParts.every((part) => Number.isSafeInteger(part) && part >= 0)
          ? oldParts.reduce((sum, part) => sum + part, 0) : null);
        setRecoveryState(data); setReplanAnchor(data.replan_anchor); setPreview(null); setCashReceived(false);
        setError('Der alte Plan ist sicher zurückgenommen. Jetzt den neu berechneten Betrag zum gespeicherten Fahrtende prüfen.');
      }
    } catch (e) {
      if (mounted.current) setError(failureText(e,
        `Rücknahme nicht bestätigt (${e.response?.data?.error || 'Antwort unklar'}). Abschlussstand erneut laden; keine Geldübergabe wiederholen.`));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const prepare = async () => {
    const trimmed = reason.trim();
    if (pending.current || !trimmed || trimmed.length > 300 || uncertain) return;
    pending.current = true;
    setBusy(true); setError(''); setPreview(null); setCashReceived(false);
    try {
      const { data } = await api.post(`/rides/${encodeURIComponent(ride.id)}/admin-completion-preview`,
        { reason: trimmed, ...(replanAnchor ? { replan_completion_id: replanAnchor.completion_id } : {}) });
      if (!mounted.current) return;
      if (!validPreview(data, ride.id, trimmed)) throw new Error('preview_invalid');
      if (replanAnchor && data.replan?.completion_id !== replanAnchor.completion_id) throw new Error('preview_replan_mismatch');
      setPreview(data);
    } catch (e) {
      if (mounted.current) setError(failureText(e,
        `Abschlussbetrag nicht bestätigt (${e.response?.data?.error || 'Antwort unklar'}). Abschlussstand prüfen.`));
      const offer = reviewOfferFrom(e.response?.data);
      if (mounted.current && offer) { setReviewOffer(offer); setReviewConfirmed(false); }
      try { await loadRecovery(); } catch { /* Sichtbarer ursprünglicher Fehler bleibt erhalten. */ }
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const endForReview = async () => {
    const trimmed = reason.trim();
    if (pending.current || !reviewOffer || !reviewConfirmed || !trimmed || trimmed.length > 300) return;
    pending.current = true; setBusy(true); setError('');
    let showPreview = false;
    try {
      const { data } = await api.post(`/rides/${encodeURIComponent(ride.id)}/admin-completion-review`,
        { reason: trimmed, confirm_review: true, ...(reviewOffer.mode === 'unsettled' ? { confirm_mode: 'unsettled' } : {}) });
      const r = data?.completion_review;
      // estimate: Abrechnung zum gebuchten Betrag freigegeben, Fahrt läuft bis zur normalen
      // Bestätigung weiter. unsettled: Fahrt ohne Betrag beendet (keine Streckenfrage).
      const estimate = r?.state === 'open' && r?.mode === 'estimate' && data?.ride?.status === 'in_progress';
      const unsettled = r?.state === 'open' && r?.mode === 'unsettled' && data?.ride?.status === 'completed';
      if (data?.ride?.id !== ride.id || (!estimate && !unsettled)) throw new Error('completion_review_response_unclear');
      if (mounted.current) { setReviewOffer(null); setReviewConfirmed(false); }
      if (unsettled && mounted.current) onCompleted();
      showPreview = estimate;
    } catch (e) {
      if (mounted.current) setError(e.response?.data?.message
        || (reviewOffer.mode === 'unsettled'
          ? `Fahrt nicht ohne Abrechnung beendet (${e.response?.data?.error || 'Antwort unklar'}). Abschlussstand prüfen.`
          : `Abrechnung zum Schätzpreis nicht freigegeben (${e.response?.data?.error || 'Antwort unklar'}). Abschlussstand prüfen.`));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
    // Danach die bestehende Vorschau: Betrag prüfen, bei Bar Empfang bestätigen, verbindlich abschließen.
    if (showPreview && mounted.current) await prepare();
  };
  const complete = async () => {
    if (pending.current || !preview || preview.ride_id !== ride.id || changeDue > 0 || (cashToConfirm > 0 && !cashReceived)) return;
    // After an ambiguous response retry exactly the same command: the server may have committed.
    if (!uncertain && Date.parse(preview.expires_at) <= Date.now()) {
      setPreview(null); setCashReceived(false); setError('Die Vorschau ist abgelaufen. Bitte den Betrag erneut prüfen.'); return;
    }
    pending.current = true;
    setBusy(true); setError('');
    try {
      const { data } = await api.patch(`/rides/${encodeURIComponent(ride.id)}/status`, {
        status: 'completed', early_reason: preview.reason,
        admin_completion_token: preview.preview_token,
        ...(cashToConfirm > 0 ? { cash_received: true } : {}),
      });
      if (data?.ride?.id !== ride.id || data?.ride?.status !== 'completed') throw new Error('completion_response_unclear');
      if (mounted.current) { setUncertain(false); onCompleted(); }
    } catch (e) {
      if (!mounted.current) return;
      const status = e.response?.status;
      if (!status || status >= 500) {
        setUncertain(true);
        setError('Die Antwort ist unklar. Bitte denselben Abschluss erneut prüfen; es wird kein zweiter Auftrag angelegt.');
      } else {
        setUncertain(false); setPreview(null); setCashReceived(false);
        setError(failureText(e,
          `Abschluss nicht bestätigt (${e.response?.data?.error || status}). Abschlussstand prüfen.`));
        try { await loadRecovery(); } catch { /* Fehler bleibt sichtbar. */ }
      }
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <section className="admin-ride-completion" aria-label="Fahrt im Admin abschließen">
      <h3>Fahrt abschließen</h3>
      <p>Serverstatus: <strong>{serverRide.status}</strong>; Zahlungsstatus: <strong>{serverRide.payment_status || 'nicht belegt'}</strong>.</p>
      {serverRide.status !== 'in_progress' ? <>
        <p>{statusGuidance[serverRide.status] || 'Kein sicherer Fahrtabschluss für diesen Zustand. Aktuellen Serverstatus und verfügbare Aktionen prüfen.'}</p>
        {openReview?.state === 'open' && openReview.mode === 'estimate' && <div className="admin-ride-completion-review" role="status">
          <p><strong>Schätzpreis, Strecke nicht nachgewiesen.</strong> Berechnet wurde der beim Buchen angebotene Betrag über die normalen Zahlungswege.</p>
          <p>Grund: {openReview.reason}. Fahrtende: {datumUndZeit(openReview.ended_at, { jahr: 'voll', sekunden: true })}.</p>
        </div>}
        {resolution && <div className="admin-ride-completion-review" role="status">
          <p><strong>{resolution.outcome === 'settled' ? `Nach Prüfung abgerechnet: ${euro(resolution.fare_cents)}.` : 'Ohne Abrechnung abgeschlossen.'}</strong>
            {resolution.outcome === 'settled' ? ` Kundenbetrag ${euro(resolution.customer_due_cents)} über die gebuchte Zahlart.` : ' Die Reserve wird freigegeben.'}</p>
          <p>Grund: {resolution.reason}. Abgeschlossen: {datumUndZeit(resolution.created_at, { jahr: 'voll', sekunden: true })}.</p>
        </div>}
        {!resolution && openReview?.state === 'open' && openReview.mode !== 'estimate' && <div className="admin-ride-completion-review" role="status">
          <p><strong>Preis in Prüfung.</strong> Die Fahrt ist beendet, abgerechnet ist noch nichts: kein Betrag gebucht, nichts eingezogen, kein Bargeld erfasst.</p>
          <p>Grund: {openReview.reason}. Fahrtende: {datumUndZeit(openReview.ended_at, { jahr: 'voll', sekunden: true })}.
            {openReview.estimate_cents != null ? (openReviewMixed
              ? ` Gebuchter Betrag ${euro(openReview.estimate_cents)} (Obergrenze für die Prüfung, kein Endpreis).`
              : ` Angebotsschätzung ${euro(openReview.estimate_cents)} (nur zur Orientierung, kein Endpreis).`) : ''}</p>
        </div>}
      </> : <>
      <p>Nur verwenden, wenn die Fahrt jetzt tatsächlich beendet ist und der Fahrer sie nicht selbst abschließen kann. Ein vorhandener Fahrerabschluss behält seinen bereits gespeicherten Betrag und Endzeitpunkt. Andernfalls hältst du mit dem Prüfen den jetzigen Zeitpunkt fest. Erst deine Bestätigung schließt die Fahrt ab. Abgerechnet wird der gebundene Fahrpreis einschließlich fälliger Wartegebühren. Eine neue Ist-Strecken-Berechnung findet hier nicht statt. Der Fahrer wird nicht automatisch offline gesetzt.</p>
      <label>Grund für den Admin-Abschluss
        <textarea aria-label="Grund für den Admin-Abschluss" maxLength={300} value={reason}
          disabled={busy || !!preview} onChange={(e) => setReason(e.target.value)} />
      </label>
      {!preview ? (
        <>
          <button type="button" disabled={busy || !reason.trim()} onClick={prepare}>
            {busy ? 'Betrag wird geprüft …' : 'Fahrtende festhalten und Betrag prüfen'}
          </button>
          <button type="button" disabled={busy} onClick={async () => {
            try { await loadRecovery(); setError(''); }
            catch (e) { setError(e.response?.data?.message || 'Abschlussstand konnte nicht geladen werden.'); }
          }}>Abschlussstand prüfen</button>
          {replanAnchor && <p>Gespeichertes Fahrtende {datumUndZeit(new Date(replanAnchor.effective_end_ms).toISOString(), { jahr: 'voll', sekunden: true })} bleibt für die neue Vorschau erhalten.</p>}
          {recoveryState?.completion?.state === 'prepared' && <div>
            <p>Vorbereiteter Plan {recoveryState.completion.id}; Barbelege: {recoveryState.completion.cash_receipt_version}. Bei geändertem Anspruch kann nur ein Plan ohne Geldbelege sicher zurückgenommen werden.</p>
            {recoveryState.completion.cash_receipt_version === 0
              ? <button type="button" disabled={busy || !reason.trim()} onClick={resumePrepared}>Gespeicherten Plan sicher zurücknehmen</button>
              : <p role="alert">Geldbelege vorhanden. Erst den tatsächlichen Erhalt oder die Rückgabe über den bestehenden Kassiervorgang klären.</p>}
          </div>}
        </>
      ) : (
        <div className="admin-ride-completion-preview">
          {preview.cash_reconciliation && <p>Die Fahrt wurde fortgesetzt. Vorhandene Barbelege werden auf diesen neu geprüften Abschluss angerechnet.</p>}
          {preview.recovery && <p>Vorbereiteten Fahrerabschluss fortsetzen. Der gespeicherte Betrag und Endzeitpunkt bleiben erhalten.</p>}
          {preview.replan && <p>Neuberechneter Abschluss zum ursprünglich gespeicherten Fahrtende. Vorher {previousAmount == null ? 'Betrag erneut anhand des Prüfprotokolls vergleichen' : euro(previousAmount)}, jetzt {euro(preview.amount_cents)}. Beide Beträge ausdrücklich prüfen.</p>}
          <p>Grund: <strong>{preview.reason}</strong></p>
          <p><strong>Fahrtende für diese Abrechnung: {datumUndZeit(preview.effective_end_at, { jahr: 'voll', sekunden: true })}</strong></p>
          <dl>
            <div><dt>Betrag für diesen Abschluss</dt><dd>{euro(preview.amount_cents)}</dd></div>
            <div><dt>Bar beim Fahrer</dt><dd>{euro(preview.cash_cents)}</dd></div>
            <div><dt>Über Karte abzurechnen</dt><dd>{euro(preview.card_cents)}</dd></div>
            <div><dt>Guthaben</dt><dd>{euro(preview.wallet_cents)}</dd></div>
            {preview.open_fee_cash_cents > 0 && <div><dt>Davon offene Gebühr in bar</dt><dd>{euro(preview.open_fee_cash_cents)}</dd></div>}
            {preview.open_fee_card_cents > 0 && <div><dt>Davon offene Gebühr per separatem Karteneinzug</dt><dd>{euro(preview.open_fee_card_cents)}</dd></div>}
            {preview.included_fee_card_cents > 0 && <div><dt>Davon eingeschlossene Gebühr per Karte</dt><dd>{euro(preview.included_fee_card_cents)}</dd></div>}
            {preview.included_paypal_tip_cents > 0 && <div><dt>Davon bestätigtes PayPal-Trinkgeld</dt><dd>{euro(preview.included_paypal_tip_cents)}</dd></div>}
          </dl>
          <p>Ein Kartenbetrag wird über den bestehenden Zahlungsablauf verarbeitet. Der Fahrtabschluss allein bestätigt noch keinen erfolgreichen Karteneinzug.</p>
          {cashState && <p>Bereits als erhalten erfasst: {euro(cashState.recorded_cash_received_cents)}. Bereits zurückgegeben: {euro(cashState.recorded_cash_returned_cents)}. Noch zu bestätigen: {euro(cashToConfirm)}.</p>}
          {changeDue > 0 && <p role="alert">Es sind {euro(changeDue)} zu viel als erhalten erfasst. Der Fahrer muss die tatsächliche Rückgabe im Kassiervorgang bestätigen, bevor der Abschluss fortgesetzt werden kann.</p>}
          {cashToConfirm > 0 && <label className="admin-ride-completion-cash">
            <input type="checkbox" checked={cashReceived} disabled={busy || uncertain}
              onChange={(e) => setCashReceived(e.target.checked)} />
            Ich bestätige, dass der Fahrer {euro(cashToConfirm)}{cashState ? ' zusätzlich zu den vorhandenen Belegen' : ''} in bar erhalten hat.
          </label>}
          <div className="admin-ride-completion-actions">
            <button type="button" disabled={busy || uncertain} onClick={() => { setPreview(null); setCashReceived(false); setError(''); }}>Zurück</button>
            <button type="button" disabled={busy || changeDue > 0 || (cashToConfirm > 0 && !cashReceived)} onClick={complete}>
              {busy ? 'Abschluss wird geprüft …' : uncertain ? 'Denselben Abschluss erneut prüfen' : 'Fahrt verbindlich abschließen'}
            </button>
          </div>
        </div>
      )}
      </>}
      {serverRide.status === 'in_progress' && !preview && reviewOffer?.mode === 'unsettled' && <div className="admin-ride-completion-review">
        <h4>Ohne Abrechnung beenden (Preis prüfen)</h4>
        {reviewOffer.code === 'essen_cash_completion_prepare_required'
          ? <p>Barfahrt mit Zieländerung ohne Kassiervorgang des Fahrers. Mit Betrag abschließen kann nur der Fahrer über seine Kassiermaske – bitte zuerst das versuchen. Nur wenn das nicht möglich ist und die Fahrt tatsächlich beendet ist: Die Fahrt endet hier ohne Betrag, nichts wird gebucht. Danach legst du in der Prüfliste den kassierten Fahrpreis fest (höchstens der gebuchte Betrag) oder schließt ohne Abrechnung ab.</p>
          : <p>Der Fahrpreis dieser Fahrt mit Zieländerung lässt sich nicht belegt berechnen{reviewOffer.reason ? ` (Grund: ${reviewOffer.reason})` : ''}{reviewOffer.code && !reviewOffer.reason ? ` (${reviewOffer.code})` : ''}. Nur verwenden, wenn die Fahrt tatsächlich beendet ist. Die Fahrt endet ohne Betrag: nichts wird gebucht oder eingezogen. Danach legst du in der Prüfliste den Fahrpreis fest (höchstens der gebuchte Betrag) oder schließt ohne Abrechnung ab. Ist der Preis doch berechenbar, lehnt der Server ab.</p>}
        <label className="admin-ride-completion-cash">
          <input type="checkbox" checked={reviewConfirmed} disabled={busy}
            onChange={(e) => setReviewConfirmed(e.target.checked)} />
          Ich bestätige: Fahrt ohne Abrechnung beenden, der Fahrpreis wird geprüft.
        </label>
        <button type="button" disabled={busy || !reviewConfirmed || !reason.trim()} onClick={endForReview}>
          {busy ? 'Wird beendet …' : 'Ohne Abrechnung beenden'}
        </button>
      </div>}
      {serverRide.status === 'in_progress' && !preview && reviewOffer && reviewOffer.mode !== 'unsettled' && <div className="admin-ride-completion-review">
        <h4>Zum Schätzpreis abrechnen</h4>
        <p>Der Fahrpreis lässt sich gerade nicht über die gefahrene Strecke belegen{reviewOffer.reason ? ` (Grund: ${reviewOffer.reason})` : ''}. Nur verwenden, wenn die Fahrt tatsächlich beendet ist. Abgerechnet wird der beim Buchen angebotene Betrag über die normalen Zahlungswege; danach erscheint wie gewohnt der Betrag zur Prüfung und Bestätigung. Ist der Preis doch berechenbar, lehnt der Server ab. Fehlt keine Strecke, sondern eine andere Bindung, endet die Fahrt ohne Betrag zur Prüfung.</p>
        <label className="admin-ride-completion-cash">
          <input type="checkbox" checked={reviewConfirmed} disabled={busy}
            onChange={(e) => setReviewConfirmed(e.target.checked)} />
          Ich bestätige: Abrechnung zum gebuchten Schätzpreis ohne Streckennachweis.
        </label>
        <button type="button" disabled={busy || !reviewConfirmed || !reason.trim()} onClick={endForReview}>
          {busy ? 'Wird freigegeben …' : 'Zum Schätzpreis abrechnen'}
        </button>
      </div>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
